import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveAnnouncer } from '../context/LiveAnnouncerContext';
import './FloatingActionButtons.css';

const WAITER_COOLDOWN_MS = 30000;

function FloatingActionButtons({ onCallWaiter, onRequestBill, showBillButton = false, cartVisible = false }) {
  const { t } = useTranslation();
  const { announce } = useLiveAnnouncer();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownEndRef = useRef(0);
  const snackbarTimerRef = useRef(null);

  const showSnackbar = useCallback((message, type = 'success') => {
    clearTimeout(snackbarTimerRef.current);
    setSnackbar({ message, type });
    snackbarTimerRef.current = setTimeout(() => setSnackbar(null), 3000);
    announce(message, type === 'error' ? 'assertive' : 'polite');
  }, [announce]);

  useEffect(() => {
    return () => clearTimeout(snackbarTimerRef.current);
  }, []);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((cooldownEndRef.current - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownRemaining]);

  const waiterOnCooldown = cooldownRemaining > 0;

  const handleCallWaiter = async () => {
    if (loading || waiterOnCooldown) return;
    setLoading(true);
    try {
      await onCallWaiter();
      showSnackbar(t('floating.waiter_called_success'));
      cooldownEndRef.current = Date.now() + WAITER_COOLDOWN_MS;
      setCooldownRemaining(Math.ceil(WAITER_COOLDOWN_MS / 1000));
    } catch {
      showSnackbar(t('floating.waiter_called_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestBill = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await onRequestBill();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`floating-action-buttons${cartVisible ? ' floating-action-buttons--cart-visible' : ''}`}>
      {snackbar && (
        <div className={`fab-snackbar fab-snackbar--${snackbar.type}`} role="status" aria-live="polite">
          <span className="material-icons fab-snackbar__icon" aria-hidden="true">
            {snackbar.type === 'success' ? 'check_circle' : 'error'}
          </span>
          {snackbar.message}
        </div>
      )}

      <button
        className={`floating-btn call-waiter-btn${waiterOnCooldown ? ' call-waiter-btn--cooldown' : ''}`}
        onClick={handleCallWaiter}
        disabled={loading || waiterOnCooldown}
        title={waiterOnCooldown
          ? t('floating.waiter_cooldown', { seconds: cooldownRemaining })
          : t('floating.call_waiter')}
        aria-label={waiterOnCooldown
          ? t('floating.waiter_cooldown', { seconds: cooldownRemaining })
          : t('floating.call_waiter')}
      >
        <span className="material-icons" aria-hidden="true">room_service</span>
        <span className="fab-label fab-label--full">
          {waiterOnCooldown
            ? t('floating.waiter_cooldown', { seconds: cooldownRemaining })
            : t('floating.call_waiter')}
        </span>
        <span className="fab-label fab-label--short">
          {waiterOnCooldown
            ? t('floating.waiter_cooldown', { seconds: cooldownRemaining })
            : t('floating.call_waiter_short')}
        </span>
        {waiterOnCooldown && (
          <span className="call-waiter-btn__countdown">{cooldownRemaining}</span>
        )}
      </button>

      {showBillButton && (
        <button
          className="floating-btn request-bill-btn"
          onClick={handleRequestBill}
          disabled={loading}
          title={t('floating.request_bill')}
          aria-label={t('floating.request_bill')}
        >
          <span className="material-icons" aria-hidden="true">receipt</span>
          <span className="fab-label fab-label--full">{t('floating.request_bill')}</span>
          <span className="fab-label fab-label--short">{t('floating.request_bill_short')}</span>
        </button>
      )}
    </div>
  );
}

export default FloatingActionButtons;
